import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import LocationSearch from '../components/LocationSearch';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  Squares2X2Icon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const RESOURCE_TYPES = [
  { value: 'field', label: 'Field' },
  { value: 'batting_cage', label: 'Batting Cage' },
  { value: 'bullpen', label: 'Bullpen' },
  { value: 'indoor_turf', label: 'Indoor Turf' },
  { value: 'gym', label: 'Gym' },
  { value: 'other', label: 'Other' }
];

// Same list as RESOURCE_TYPES, plus "park" for the top-level/no-parent case — used by the
// unified Add Facility modal so a park and the resources within it can be created from one place.
const FACILITY_TYPE_OPTIONS = [
  { value: 'park', label: 'Park / Venue (top-level, has an address)' },
  ...RESOURCE_TYPES
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const emptyFacilityForm = {
  name: '',
  facility_type: 'park',
  parent_facility_id: '',
  addressText: '',
  address: null,
  description: '',
  age_group_ids: [],
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  parking_notes: ''
};
const emptyResourceForm = { name: '', resource_type: 'field', age_group_ids: [], parent_resource_id: '' };
const emptyWindowForm = { day_of_week: '1', start_time: '17:00', end_time: '20:00' };
const emptyBlackoutForm = { facility_resource_id: '', date: '', start_time: '', end_time: '', reason: '' };

const Facilities = () => {
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [schedulingEnabled, setSchedulingEnabled] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showFacilityForm, setShowFacilityForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [facilityForm, setFacilityForm] = useState(emptyFacilityForm);

  const [expandedFacilityId, setExpandedFacilityId] = useState(null);
  const [upcomingEventsFacilityId, setUpcomingEventsFacilityId] = useState(null);
  const [upcomingEventsByFacility, setUpcomingEventsByFacility] = useState({});
  const [upcomingEventsLoading, setUpcomingEventsLoading] = useState(false);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState(emptyResourceForm);

  const [expandedResourceId, setExpandedResourceId] = useState(null);
  const [windowForm, setWindowForm] = useState(emptyWindowForm);

  const [showBlackoutForm, setShowBlackoutForm] = useState(false);
  const [blackoutForm, setBlackoutForm] = useState(emptyBlackoutForm);

  useEffect(() => {
    if (!token) return;
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get('/api/organizations/my-organizations', { headers: authHeaders });
      const orgs = Array.isArray(response.data) ? response.data : [];
      const transformed = orgs.map(orgUser => (orgUser.Organization
        ? { id: orgUser.Organization.id, name: orgUser.Organization.name }
        : orgUser));
      setOrganizations(transformed);
      if (transformed.length > 0 && !selectedOrg) {
        setSelectedOrg(transformed[0].id.toString());
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  };

  const fetchFacilities = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/facilities?organization_id=${selectedOrg}`, { headers: authHeaders });
      setFacilities(response.data?.facilities || []);
      setSchedulingEnabled(!!response.data?.schedulingEnabled);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setFacilities([]);
      setSchedulingEnabled(error.response?.status !== 403);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrg]);

  const toggleUpcomingEvents = async (facilityId) => {
    if (upcomingEventsFacilityId === facilityId) {
      setUpcomingEventsFacilityId(null);
      return;
    }
    setUpcomingEventsFacilityId(facilityId);
    if (upcomingEventsByFacility[facilityId]) return;
    try {
      setUpcomingEventsLoading(true);
      const response = await axios.get(`/api/facilities/${facilityId}/upcoming-events`, { headers: authHeaders });
      setUpcomingEventsByFacility(prev => ({ ...prev, [facilityId]: Array.isArray(response.data) ? response.data : [] }));
    } catch (error) {
      toast.error('Failed to load upcoming events');
      setUpcomingEventsByFacility(prev => ({ ...prev, [facilityId]: [] }));
    } finally {
      setUpcomingEventsLoading(false);
    }
  };

  const fetchAgeGroups = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      const response = await axios.get(`/api/age-groups?organizationId=${selectedOrg}`, { headers: authHeaders });
      setAgeGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setAgeGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrg]);

  useEffect(() => {
    fetchFacilities();
    fetchAgeGroups();
    setExpandedFacilityId(null);
    setExpandedResourceId(null);
  }, [fetchFacilities, fetchAgeGroups]);

  // ---- Facility CRUD ----

  const openCreateFacility = () => {
    setEditingFacility(null);
    setFacilityForm(emptyFacilityForm);
    setShowFacilityForm(true);
  };

  const openEditFacility = (facility) => {
    setEditingFacility(facility);
    setFacilityForm({
      name: facility.name || '',
      facility_type: 'park',
      parent_facility_id: '',
      addressText: facility.address?.formatted || facility.address?.address || '',
      address: facility.address || null,
      description: facility.description || '',
      age_group_ids: [],
      contact_name: facility.contact_name || '',
      contact_phone: facility.contact_phone || '',
      contact_email: facility.contact_email || '',
      parking_notes: facility.parking_notes || ''
    });
    setShowFacilityForm(true);
  };

  const toggleFacilityFormAgeGroup = (ageGroupId) => {
    setFacilityForm(prev => {
      const set = new Set(prev.age_group_ids);
      if (set.has(ageGroupId)) set.delete(ageGroupId); else set.add(ageGroupId);
      return { ...prev, age_group_ids: Array.from(set) };
    });
  };

  const submitFacility = async (e) => {
    e.preventDefault();
    if (!facilityForm.name.trim()) {
      toast.error('Name is required.');
      return;
    }

    // Editing is always a park (top-level Facility); creating branches on the chosen type.
    if (editingFacility || facilityForm.facility_type === 'park') {
      try {
        const payload = {
          name: facilityForm.name.trim(),
          address: facilityForm.address || (facilityForm.addressText ? { formatted: facilityForm.addressText } : null),
          description: facilityForm.description.trim() || null,
          contact_name: facilityForm.contact_name.trim() || null,
          contact_phone: facilityForm.contact_phone.trim() || null,
          contact_email: facilityForm.contact_email.trim() || null,
          parking_notes: facilityForm.parking_notes.trim() || null
        };
        if (editingFacility) {
          await axios.put(`/api/facilities/${editingFacility.id}`, payload, { headers: authHeaders });
          toast.success('Facility updated');
        } else {
          await axios.post('/api/facilities', { ...payload, organization_id: parseInt(selectedOrg, 10) }, { headers: authHeaders });
          toast.success('Park created');
        }
        setShowFacilityForm(false);
        setEditingFacility(null);
        fetchFacilities();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to save facility');
      }
      return;
    }

    if (!facilityForm.parent_facility_id) {
      toast.error('Select the park this belongs to.');
      return;
    }
    try {
      await axios.post(`/api/facilities/${facilityForm.parent_facility_id}/resources`, {
        name: facilityForm.name.trim(),
        resource_type: facilityForm.facility_type,
        age_group_ids: facilityForm.age_group_ids
      }, { headers: authHeaders });
      toast.success('Facility added');
      setShowFacilityForm(false);
      setExpandedFacilityId(parseInt(facilityForm.parent_facility_id, 10));
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save facility');
    }
  };

  const deleteFacility = async (facility) => {
    if (!window.confirm(`Remove facility "${facility.name}"? Its resources will be deactivated too.`)) return;
    try {
      await axios.delete(`/api/facilities/${facility.id}`, { headers: authHeaders });
      toast.success('Facility removed');
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove facility');
    }
  };

  // ---- Resource CRUD ----

  const openCreateResource = (facilityId) => {
    setExpandedFacilityId(facilityId);
    setEditingResource(null);
    setResourceForm(emptyResourceForm);
    setShowResourceForm(true);
  };

  const openEditResource = (resource, facilityId) => {
    setExpandedFacilityId(facilityId);
    setEditingResource(resource);
    setResourceForm({
      name: resource.name || '',
      resource_type: resource.resource_type || 'field',
      age_group_ids: (resource.RestrictedAgeGroups || []).map(ag => ag.id),
      parent_resource_id: resource.parent_resource_id ? String(resource.parent_resource_id) : ''
    });
    setShowResourceForm(true);
  };

  const toggleResourceAgeGroup = (ageGroupId) => {
    setResourceForm(prev => {
      const set = new Set(prev.age_group_ids);
      if (set.has(ageGroupId)) set.delete(ageGroupId); else set.add(ageGroupId);
      return { ...prev, age_group_ids: Array.from(set) };
    });
  };

  const submitResource = async (e, facilityId) => {
    e.preventDefault();
    if (!resourceForm.name.trim()) {
      toast.error('Resource name is required.');
      return;
    }
    try {
      const payload = {
        name: resourceForm.name.trim(),
        resource_type: resourceForm.resource_type,
        age_group_ids: resourceForm.age_group_ids,
        parent_resource_id: resourceForm.parent_resource_id || null
      };
      if (editingResource) {
        await axios.put(`/api/facilities/resources/${editingResource.id}`, payload, { headers: authHeaders });
        toast.success('Resource updated');
      } else {
        await axios.post(`/api/facilities/${facilityId}/resources`, payload, { headers: authHeaders });
        toast.success('Resource added');
      }
      setShowResourceForm(false);
      setEditingResource(null);
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save resource');
    }
  };

  const deleteResource = async (resource) => {
    if (!window.confirm(`Remove resource "${resource.name}"?`)) return;
    try {
      await axios.delete(`/api/facilities/resources/${resource.id}`, { headers: authHeaders });
      toast.success('Resource removed');
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove resource');
    }
  };

  // ---- Availability windows ----

  const addWindow = async (e, resourceId) => {
    e.preventDefault();
    try {
      await axios.post(`/api/facilities/resources/${resourceId}/availability-windows`, {
        day_of_week: parseInt(windowForm.day_of_week, 10),
        start_time: windowForm.start_time,
        end_time: windowForm.end_time
      }, { headers: authHeaders });
      setWindowForm(emptyWindowForm);
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add availability window');
    }
  };

  const removeWindow = async (windowId) => {
    try {
      await axios.delete(`/api/facilities/availability-windows/${windowId}`, { headers: authHeaders });
      fetchFacilities();
    } catch (error) {
      toast.error('Failed to remove availability window');
    }
  };

  // ---- Blackouts ----

  const submitBlackout = async (e, facilityId) => {
    e.preventDefault();
    if (!blackoutForm.date) {
      toast.error('A date is required.');
      return;
    }
    try {
      await axios.post(`/api/facilities/${facilityId}/blackouts`, {
        facility_resource_id: blackoutForm.facility_resource_id || null,
        date: blackoutForm.date,
        start_time: blackoutForm.start_time || null,
        end_time: blackoutForm.end_time || null,
        reason: blackoutForm.reason.trim() || null
      }, { headers: authHeaders });
      toast.success('Blackout added');
      setBlackoutForm(emptyBlackoutForm);
      setShowBlackoutForm(false);
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add blackout');
    }
  };

  const removeBlackout = async (blackoutId) => {
    try {
      await axios.delete(`/api/facilities/blackouts/${blackoutId}`, { headers: authHeaders });
      fetchFacilities();
    } catch (error) {
      toast.error('Failed to remove blackout');
    }
  };

  const resourceTypeLabel = (value) => RESOURCE_TYPES.find(t => t.value === value)?.label || value;

  const currentFacility = facilities.find(f => f.id === expandedFacilityId);
  const parentCandidates = (currentFacility?.Resources || []).filter(r =>
    r.is_active && !r.parent_resource_id && (!editingResource || r.id !== editingResource.id)
  );
  const editingResourceHasChildren = !!editingResource && (currentFacility?.Resources || [])
    .some(r => r.parent_resource_id === editingResource.id);

  const renderResource = (resource, facility, depth = 0) => {
    const children = (facility.Resources || []).filter(r => r.is_active && r.parent_resource_id === resource.id);
    return (
      <div key={resource.id} className={depth > 0 ? 'ml-6 mt-2' : ''}>
        <div className="bg-white rounded border border-gray-200">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="text-sm">
              {depth > 0 && <span className="text-gray-300 mr-1">↳</span>}
              <span className="font-medium text-gray-900">{resource.name}</span>
              <span className="ml-2 text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                {resourceTypeLabel(resource.resource_type)}
              </span>
              {(resource.RestrictedAgeGroups || []).length === 0 ? (
                <span className="ml-2 text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">Open to all ages</span>
              ) : (
                resource.RestrictedAgeGroups.map(ag => (
                  <span key={ag.id} className="ml-1 text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded">{ag.name}</span>
                ))
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setExpandedResourceId(v => v === resource.id ? null : resource.id)}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                {expandedResourceId === resource.id ? 'Hide availability' : 'Availability'}
              </button>
              <button onClick={() => openEditResource(resource, facility.id)} className="p-1.5 text-gray-400 hover:text-gray-700" title="Edit">
                <PencilIcon className="h-4 w-4" />
              </button>
              <button onClick={() => deleteResource(resource)} className="p-1.5 text-gray-400 hover:text-red-600" title="Remove">
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {expandedResourceId === resource.id && (
            <div className="border-t border-gray-100 px-3 py-3 bg-gray-50 space-y-2">
              {(resource.AvailabilityWindows || []).length === 0 ? (
                <p className="text-xs text-gray-500">No weekly availability set yet.</p>
              ) : (
                <div className="space-y-1">
                  {resource.AvailabilityWindows.map(w => (
                    <div key={w.id} className="flex items-center justify-between text-xs bg-white rounded border border-gray-200 px-2 py-1">
                      <span>{DAYS[w.day_of_week]}: {w.start_time?.slice(0, 5)} - {w.end_time?.slice(0, 5)}</span>
                      <button onClick={() => removeWindow(w.id)} className="text-gray-400 hover:text-red-600">
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={(e) => addWindow(e, resource.id)} className="flex flex-wrap items-end gap-2">
                <select
                  value={windowForm.day_of_week}
                  onChange={(e) => setWindowForm(f => ({ ...f, day_of_week: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded"
                >
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
                <input type="time" value={windowForm.start_time}
                  onChange={(e) => setWindowForm(f => ({ ...f, start_time: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded" />
                <span className="text-xs text-gray-400">to</span>
                <input type="time" value={windowForm.end_time}
                  onChange={(e) => setWindowForm(f => ({ ...f, end_time: e.target.value }))}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded" />
                <button type="submit" className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700">
                  Add window
                </button>
              </form>
            </div>
          )}
        </div>

        {children.length > 0 && (
          <div className="space-y-2 mt-2">
            {children.map(child => renderResource(child, facility, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MapPinIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facilities</h1>
            <p className="text-sm text-gray-500">Manage parks, fields, cages, and other bookable resources.</p>
          </div>
        </div>
        <button
          onClick={openCreateFacility}
          disabled={!selectedOrg || !schedulingEnabled}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <PlusIcon className="h-5 w-5" /> Add Facility
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select an organization</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      {selectedOrg && !schedulingEnabled ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Scheduling is not enabled for this organization. Contact your app administrator to turn it on.
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-500">Loading facilities...</div>
      ) : facilities.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          No facilities yet. Add a park or venue to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {facilities.map(facility => (
            <div key={facility.id} className="bg-white rounded-lg shadow">
              <div className="p-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{facility.name}</h3>
                  {(facility.address?.formatted || facility.address?.address) && (
                    <p className="text-sm text-gray-500">{facility.address.formatted || facility.address.address}</p>
                  )}
                  {facility.description && <p className="text-sm text-gray-600 mt-1">{facility.description}</p>}
                  {(facility.contact_name || facility.contact_phone || facility.contact_email) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {[facility.contact_name, facility.contact_phone, facility.contact_email].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {facility.parking_notes && (
                    <p className="text-xs text-gray-500 mt-1"><span className="font-medium">Parking:</span> {facility.parking_notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => openEditFacility(facility)} className="p-2 text-gray-500 hover:text-gray-700" title="Edit">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => deleteFacility(facility)} className="p-2 text-gray-500 hover:text-red-600" title="Remove">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-100 px-4 py-2 flex items-center gap-4">
                <button
                  onClick={() => setExpandedFacilityId(v => v === facility.id ? null : facility.id)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {expandedFacilityId === facility.id ? 'Hide resources' : `Manage resources (${facility.Resources?.length || 0})`}
                </button>
                <button
                  onClick={() => toggleUpcomingEvents(facility.id)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {upcomingEventsFacilityId === facility.id ? 'Hide upcoming events' : 'Upcoming events'}
                </button>
                <Link
                  to={`/facility/${facility.id}`}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  <WrenchScrewdriverIcon className="h-4 w-4" />
                  Lost & Found / Maintenance
                </Link>
              </div>

              {upcomingEventsFacilityId === facility.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  {upcomingEventsLoading && !upcomingEventsByFacility[facility.id] ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                  ) : (upcomingEventsByFacility[facility.id] || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Nothing scheduled here yet.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                      {upcomingEventsByFacility[facility.id].map(ev => (
                        <div key={ev.id} className="flex items-center justify-between text-sm bg-white rounded border border-gray-200 px-3 py-2">
                          <span>
                            <span className="font-medium text-gray-900">{ev.Team?.name}</span>
                            {ev.AwayTeam && <><span className="text-gray-400"> vs </span><span className="font-medium text-gray-900">{ev.AwayTeam.name}</span></>}
                            <span className="text-gray-500"> — {ev.date} {ev.time?.slice(0, 5)}</span>
                          </span>
                          <span className="text-xs text-gray-500">{ev.FacilityResource?.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {expandedFacilityId === facility.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-800 inline-flex items-center gap-1">
                      <Squares2X2Icon className="h-5 w-5" /> Resources
                    </h4>
                    <button
                      onClick={() => openCreateResource(facility.id)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      + Add resource
                    </button>
                  </div>

                  {(facility.Resources || []).filter(r => r.is_active && !r.parent_resource_id).length === 0 ? (
                    <p className="text-sm text-gray-500">No resources yet. Add a field, cage, or bullpen.</p>
                  ) : (
                    <div className="space-y-2">
                      {facility.Resources.filter(r => r.is_active && !r.parent_resource_id).map(resource => renderResource(resource, facility))}
                    </div>
                  )}

                  {/* Blackouts */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-800">Blackout Dates</h4>
                      <button
                        onClick={() => { setShowBlackoutForm(v => !v); setBlackoutForm(emptyBlackoutForm); }}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {showBlackoutForm ? 'Cancel' : '+ Add blackout'}
                      </button>
                    </div>

                    {(facility.Blackouts || []).length === 0 ? (
                      <p className="text-sm text-gray-500">No blackout dates set.</p>
                    ) : (
                      <div className="space-y-1">
                        {facility.Blackouts.map(b => (
                          <div key={b.id} className="flex items-center justify-between text-sm bg-white rounded border border-gray-200 px-3 py-2">
                            <span>{b.date}{b.start_time ? ` ${b.start_time.slice(0, 5)}-${b.end_time?.slice(0, 5)}` : ' (all day)'} {b.reason && `- ${b.reason}`} <span className="text-xs text-gray-400">(whole facility)</span></span>
                            <button onClick={() => removeBlackout(b.id)} className="text-gray-400 hover:text-red-600">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {showBlackoutForm && (
                      <form onSubmit={(e) => submitBlackout(e, facility.id)} className="mt-3 bg-white rounded border border-gray-200 p-3 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Resource (optional)</label>
                            <select
                              value={blackoutForm.facility_resource_id}
                              onChange={(e) => setBlackoutForm(f => ({ ...f, facility_resource_id: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="">Whole facility</option>
                              {(facility.Resources || []).filter(r => r.is_active).map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                            <input type="date" value={blackoutForm.date}
                              onChange={(e) => setBlackoutForm(f => ({ ...f, date: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Start time (blank = all day)</label>
                            <input type="time" value={blackoutForm.start_time}
                              onChange={(e) => setBlackoutForm(f => ({ ...f, start_time: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">End time</label>
                            <input type="time" value={blackoutForm.end_time}
                              onChange={(e) => setBlackoutForm(f => ({ ...f, end_time: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                          <input value={blackoutForm.reason}
                            onChange={(e) => setBlackoutForm(f => ({ ...f, reason: e.target.value }))}
                            placeholder="Rainout, holiday, maintenance..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                        </div>
                        <div className="flex justify-end">
                          <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">Add blackout</button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Facility create/edit modal — unified: "Park" creates a top-level Facility (with an
          address); any other type creates a FacilityResource within a chosen parent park
          (with age-group restrictions), matching how these are actually stored. */}
      {showFacilityForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{editingFacility ? 'Edit Park' : 'Add Facility'}</h2>
            </div>
            <form onSubmit={submitFacility} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input value={facilityForm.name} onChange={(e) => setFacilityForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Riverside Park" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={facilityForm.facility_type} disabled={!!editingFacility}
                  onChange={(e) => setFacilityForm(f => ({ ...f, facility_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500">
                  {FACILITY_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              {facilityForm.facility_type === 'park' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <LocationSearch
                      value={facilityForm.addressText}
                      onChange={(value) => setFacilityForm(f => ({ ...f, addressText: value }))}
                      placeholder="Search for an address"
                      onSelect={(location) => {
                        const formatted = location.displayName || location.name || location.address;
                        setFacilityForm(f => ({
                          ...f,
                          addressText: formatted,
                          address: { formatted, coordinates: location.coordinates }
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={facilityForm.description} onChange={(e) => setFacilityForm(f => ({ ...f, description: e.target.value }))} rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">Contact & logistics (optional)</h3>
                    <p className="text-xs text-gray-500 mb-3">Shown to org staff managing this facility, and useful to pass along to parents/players.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Contact name</label>
                        <input value={facilityForm.contact_name} onChange={(e) => setFacilityForm(f => ({ ...f, contact_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Field coordinator" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Contact phone</label>
                        <input value={facilityForm.contact_phone} onChange={(e) => setFacilityForm(f => ({ ...f, contact_phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="(555) 555-1234" />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Contact email</label>
                      <input type="email" value={facilityForm.contact_email} onChange={(e) => setFacilityForm(f => ({ ...f, contact_email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="parks@example.com" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Parking / entrance notes</label>
                      <textarea value={facilityForm.parking_notes} onChange={(e) => setFacilityForm(f => ({ ...f, parking_notes: e.target.value }))} rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Enter via the north gate; overflow parking across the street." />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent facility (the park it's in) *</label>
                    <select value={facilityForm.parent_facility_id}
                      onChange={(e) => setFacilityForm(f => ({ ...f, parent_facility_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="">
                        {facilities.length === 0 ? 'No parks yet — create one first' : 'Select a park'}
                      </option>
                      {facilities.filter(f => f.is_active).map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age group restrictions</label>
                    <p className="text-xs text-gray-500 mb-2">Leave all unchecked to make this open to every age group.</p>
                    {ageGroups.length === 0 ? (
                      <p className="text-sm text-gray-500">No age groups configured for this organization.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {ageGroups.map(ag => (
                          <label key={ag.id} className="flex items-center text-sm text-gray-700">
                            <input type="checkbox" checked={facilityForm.age_group_ids.includes(ag.id)}
                              onChange={() => toggleFacilityFormAgeGroup(ag.id)}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                            <span className="ml-2">{ag.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowFacilityForm(false); setEditingFacility(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  {editingFacility ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resource create/edit modal */}
      {showResourceForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{editingResource ? 'Edit Resource' : 'New Resource'}</h2>
            </div>
            <form onSubmit={(e) => submitResource(e, expandedFacilityId)} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input value={resourceForm.name} onChange={(e) => setResourceForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Field 1" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={resourceForm.resource_type} onChange={(e) => setResourceForm(f => ({ ...f, resource_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  {RESOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent resource</label>
                {editingResourceHasChildren ? (
                  <p className="text-xs text-gray-500">
                    This resource has its own child resources, so it can't be nested under another resource.
                  </p>
                ) : (
                  <>
                    <select value={resourceForm.parent_resource_id}
                      onChange={(e) => setResourceForm(f => ({ ...f, parent_resource_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="">None — lives directly in the park</option>
                      {parentCandidates.map(r => (
                        <option key={r.id} value={r.id}>{r.name} ({resourceTypeLabel(r.resource_type)})</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">e.g. a batting cage that lives within a field.</p>
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age group restrictions</label>
                <p className="text-xs text-gray-500 mb-2">Leave all unchecked to make this resource open to every age group.</p>
                {ageGroups.length === 0 ? (
                  <p className="text-sm text-gray-500">No age groups configured for this organization.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {ageGroups.map(ag => (
                      <label key={ag.id} className="flex items-center text-sm text-gray-700">
                        <input type="checkbox" checked={resourceForm.age_group_ids.includes(ag.id)}
                          onChange={() => toggleResourceAgeGroup(ag.id)}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                        <span className="ml-2">{ag.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowResourceForm(false); setEditingResource(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  {editingResource ? 'Save' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Facilities;
